import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Flame, X, CalendarDays } from "lucide-react";
import { useProgress } from "../context/ProgressContext";
import { computeStreak, daysSince } from "../lib/streak";
import { lessonDayStatus } from "../lib/lessonDay";
import Panda from "./Panda";
import StreakRescueButton from "./StreakRescueButton";

// Friendly in-app reminder shown when the active child hasn't done a lesson step this week.
const StreakReminder = () => {
  const { activeChild } = useProgress();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = React.useState(() => sessionStorage.getItem("tycc_streak_dismissed") === "1");

  if (!activeChild || dismissed) return null;
  if (pathname.startsWith("/lessons/") || pathname.startsWith("/certificate")) return null;

  const { weeks, activeThisWeek, lastActive } = computeStreak(activeChild.activity || []);
  const hasHistory = (activeChild.activity || []).length > 0;
  const ld = lessonDayStatus(activeChild.lessonDay, activeChild.activity || []);
  const dismiss = () => {
    sessionStorage.setItem("tycc_streak_dismissed", "1");
    setDismissed(true);
  };

  // Lesson-day nudge takes priority: it's the chosen day and nothing has been done yet today
  if (ld?.isToday && !ld.doneToday) {
    return (
      <div className="bg-[#e9f9fd] border-b-2 border-[#6dcdf2]" data-testid="lesson-day-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 sm:gap-4">
          <div className="hidden sm:block"><Panda size={40} accessory={activeChild.panda.accessory} scarf={activeChild.panda.scarf} animate /></div>
          <CalendarDays className="text-[#1b8bc9] flex-shrink-0" size={22} />
          <p className="flex-1 text-sm text-[#0c4a6e]">
            It's <span className="font-bold">{ld.dayName}</span> - <span className="font-bold">{activeChild.panda.name}'s</span> lesson day! One step keeps the routine (and the streak) going.
          </p>
          <Link to="/lessons" className="btn-pill bg-[#1b8bc9] hover:bg-[#156f9f] text-white text-sm px-4 py-1.5 flex-shrink-0" data-testid="lesson-day-cta">Start today's step</Link>
          <button onClick={dismiss} aria-label="Dismiss" className="text-[#0c4a6e] hover:text-[#e5356f] p-1"><X size={18} /></button>
        </div>
      </div>
    );
  }

  if (activeThisWeek) return null;
  if (!hasHistory) return null; // brand new child - no nagging yet

  const since = daysSince(lastActive);

  return (
    <div className="bg-[#fff8e1] border-b-2 border-[#f3c932]" data-testid="streak-reminder">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 sm:gap-4">
        <div className="hidden sm:block"><Panda size={40} accessory={activeChild.panda.accessory} scarf={activeChild.panda.scarf} /></div>
        <Flame className="text-[#f7931e] flex-shrink-0" fill="#f7931e" size={22} />
        <p className="flex-1 text-sm text-[#5a4600]">
          <span className="font-bold">{activeChild.panda.name}</span> hasn't done a lesson step this week
          {since !== null && since > 0 ? ` (last one ${since} day${since === 1 ? "" : "s"} ago)` : ""}.
          {weeks > 0 ? ` Keep the ${weeks}-week streak alive - one step is all it takes!` : " A quick step today starts a new weekly streak!"}
        </p>
        <Link to="/lessons" className="btn-pill bg-[#f7931e] hover:bg-[#dd7f0f] text-white text-sm px-4 py-1.5 flex-shrink-0" data-testid="streak-reminder-cta">Do a step</Link>
        <span className="hidden sm:inline-block flex-shrink-0"><StreakRescueButton child={activeChild} /></span>
        <button onClick={dismiss} aria-label="Dismiss reminder" className="text-[#8a6d00] hover:text-[#5a4600] p-1" data-testid="streak-reminder-dismiss"><X size={18} /></button>
      </div>
    </div>
  );
};

export default StreakReminder;
